import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { EnergyLabelService, EnergyLabelSortPipe } from '@cfp/shared/energy-label';
import { Legend, LegendItem } from '@cfp/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LabelDistributionLabelValueDTO, LabelDistributionResponseDTO, LabelGroupDTO } from 'csr-asset-app';
import { SORTED_EPC_LABELS_AND_GROUP } from '../../energy-label/types';
import { EnergyLabelDistribution, EnergyLabelDistributionItem } from './energy-label-distribution';

function normalizeEpcLabelForA(label: string): string {
  // Greek Α (U+0391) / α (U+03B1)
  return label.replaceAll('\u0391', 'A').replaceAll('\u03b1', 'a');
}

@Component({
  selector: 'cfp-portfolio-energy-label-ratings',
  styleUrls: ['energy-label-ratings.scss'],
  templateUrl: 'energy-label-ratings.html',
  imports: [TranslateModule, EnergyLabelDistribution, Legend, LegendItem],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioEnergyLabelRatings {
  public readonly labelGroup = input.required<LabelGroupDTO>();
  public readonly labelDistribution = input.required<LabelDistributionResponseDTO>();
  public readonly title = input<string>('energyLabel.ratings.title');
  public readonly description = input<string>();
  public readonly subDescription = input<string>();
  public readonly combineAOrHigher = input<boolean>(true);
  public readonly showLabel = input<boolean>(false);
  public readonly currentDistributionTitle = input<string>('energyLabel.ratings.currentDistribution');
  public readonly improvedDistributionTitle = input<string>('energyLabel.ratings.improvedDistribution');

  protected readonly LabelGroupDTO = LabelGroupDTO;

  protected readonly labelGroupClass = computed(() => this.labelGroup()?.toLowerCase());

  private labelDistributionEnergyLabel = computed(() => {
    const labelDistribution = this.labelDistribution();
    const labelGroup = this.labelGroup();
    if (!labelDistribution || !labelGroup) {
      return undefined;
    }
    return labelDistribution.energyLabels[labelGroup];
  });
  private labelDistributionImprovedEnergyLabel = computed(() => {
    const labelDistribution = this.labelDistribution();
    const labelGroup = this.labelGroup();
    if (!labelDistribution || !labelGroup) {
      return undefined;
    }
    return labelDistribution.improvedEnergyLabels[labelGroup];
  });

  protected readonly currentPercentageGreenLabels = computed(
    () => this.labelDistributionEnergyLabel()?.percentageGreenLabels
  );
  protected readonly energyLabels = computed(() => {
    const labelDistributionEnergyLabel = this.labelDistributionEnergyLabel();
    const labelGroup = this.labelGroup();
    if (!labelDistributionEnergyLabel || !labelGroup) {
      return undefined;
    }

    return this.getEnergyLabels(labelGroup, labelDistributionEnergyLabel.labels);
  });

  protected readonly improvedPercentageGreenLabels = computed(
    () => this.labelDistributionImprovedEnergyLabel()?.percentageGreenLabels
  );
  protected readonly improvedEnergyLabels = computed(() => {
    const labelDistributionImprovedEnergyLabel = this.labelDistributionImprovedEnergyLabel();
    const labelGroup = this.labelGroup();
    if (!labelDistributionImprovedEnergyLabel || !labelGroup) {
      return undefined;
    }

    return this.getEnergyLabels(labelGroup, labelDistributionImprovedEnergyLabel.labels);
  });

  private readonly hasHigherThanALabels = computed(() => {
    if (!this.combineAOrHigher() || this.labelGroup() !== LabelGroupDTO.EPC) {
      return false;
    }
    const distribution = this.labelDistribution();
    const group = this.labelGroup();
    if (!distribution || !group) {
      return false;
    }
    const currentLabels = distribution.energyLabels[group]?.labels ?? [];
    const improvedLabels = distribution.improvedEnergyLabels[group]?.labels ?? [];
    const hasAPlusRating = (labels: LabelDistributionLabelValueDTO[]) =>
      labels.some((energyLabel) => {
        const label = normalizeEpcLabelForA(energyLabel.label).toLowerCase();
        return label.includes('a') && label.includes('+');
      });
    return hasAPlusRating(currentLabels) || hasAPlusRating(improvedLabels);
  });

  public readonly legendEnergyLabels = computed(() => {
    const labelGroup = this.labelGroup();
    const energyLabels = this.energyLabels();
    const improvedEnergyLabels = this.improvedEnergyLabels();
    if (!energyLabels || !improvedEnergyLabels) {
      return undefined;
    }
    const energyLabelsSet = new Set(energyLabels.map((energyLabel) => energyLabel.label));
    const merged = this.energyLabelSort.transform(
      [...energyLabels, ...improvedEnergyLabels.filter((energyLabel) => !energyLabelsSet.has(energyLabel.label))],
      labelGroup,
      'label',
      true
    );

    const mapped = merged.map((energyLabel) => {
      if (this.combineAOrHigher() && labelGroup === LabelGroupDTO.EPC) {
        const translation =
          energyLabel.epcAOrHigherBand && this.hasHigherThanALabels()
            ? this.translateService.instant('energyLabel.EPC.aOrHigher')
            : energyLabel.translation;
        return { ...energyLabel, translation };
      }
      return { ...energyLabel };
    });

    if (labelGroup !== LabelGroupDTO.EPC || !this.combineAOrHigher() || !this.hasHigherThanALabels()) {
      return mapped;
    }

    let keptAOrHigherLegend = false;
    return mapped.filter((energyLabel) => {
      if (energyLabel.epcAOrHigherBand) {
        if (keptAOrHigherLegend) {
          return false;
        }
        keptAOrHigherLegend = true;
      }
      return true;
    });
  });

  private readonly energyLabelSort = inject(EnergyLabelSortPipe);
  private readonly translateService = inject(TranslateService);
  private readonly energyLabelService = inject(EnergyLabelService);

  /** Map token from `SORTED_EPC_LABELS_AND_GROUP` to Latin `a` for Greek alpha tiers. */
  private normalizeEpcColorGroupFromMap(group: string | undefined): string | undefined {
    if (group == null || group === '') {
      return group;
    }
    if (group === '\u03b1+' || group === '\u03b1') {
      return 'a';
    }
    return group;
  }

  private getEpcLabelColorGroupFromSortedMap(label: string): string | undefined {
    return this.normalizeEpcColorGroupFromMap(SORTED_EPC_LABELS_AND_GROUP.get(label));
  }

  private isEpcAOrHigherLetterScaleLabel(label: string): boolean {
    if (this.getEpcLabelColorGroupFromSortedMap(label) !== 'a') {
      return false;
    }
    // Exclude DE/PL `0-100` / `0-50`.
    return label !== '0-100' && label !== '0-50';
  }

  private getEnergyLabels(
    labelGroup: LabelGroupDTO,
    energyLabels: LabelDistributionLabelValueDTO[]
  ): EnergyLabelDistributionItem[] {
    switch (labelGroup) {
      case LabelGroupDTO.EPC:
        return this.getEneryLabelsEPC(labelGroup, energyLabels);
      case LabelGroupDTO.NABERS:
        return this.getEneryLabelsNABERS(labelGroup, energyLabels);
      default:
        return energyLabels.map((energyLabel) => {
          return {
            percentage: energyLabel.percentage,
            colorClass: this.energyLabelService.getBackgroundColorClass(labelGroup, energyLabel.label),
            label: energyLabel.label,
            translation: this.energyLabelService.getTranslatedLabel(labelGroup, energyLabel.label)
          };
        });
    }
  }

  private getEneryLabelsNABERS(
    labelGroup: LabelGroupDTO,
    energyLabels: LabelDistributionLabelValueDTO[]
  ): EnergyLabelDistributionItem[] {
    return this.energyLabelSort.transform(
      energyLabels.map((energyLabel) => {
        const translation =
          energyLabel.label === 'none'
            ? this.translateService.instant('energyLabel.NABERS.none')
            : energyLabel.label === '0.5' || energyLabel.label === '1'
              ? this.translateService.instant('energyLabel.NABERS.star', { stars: energyLabel.label })
              : this.translateService.instant('energyLabel.NABERS.stars', { stars: energyLabel.label });

        return {
          percentage: energyLabel.percentage,
          colorClass: `energy-label-nabers-bg-${energyLabel.label.replace('.', '')}`,
          label: energyLabel.label,
          translation: translation
        };
      }),
      labelGroup,
      'label',
      true
    );
  }

  private getEneryLabelsEPC(
    labelGroup: LabelGroupDTO,
    energyLabels: LabelDistributionLabelValueDTO[]
  ): EnergyLabelDistributionItem[] {
    let hasOneOrMoreLabelsHigherThanA: boolean = false;
    const reducedEnergyLabels = energyLabels.reduce(
      (map: { [key: string]: LabelDistributionLabelValueDTO }, energyLabel: LabelDistributionLabelValueDTO) => {
        let label = normalizeEpcLabelForA(energyLabel.label).toLowerCase();
        let hasAPlusLabel = label.includes('a') && label.includes('+');
        const doesNotHaveNumeralLabel = label.search(/^(?:\d{3}-\d{3}|>\d{3,4})$/) === -1;

        if (this.combineAOrHigher()) {
          if (hasAPlusLabel) {
            label = 'a';
            hasOneOrMoreLabelsHigherThanA = true;
          }
        } else if (doesNotHaveNumeralLabel) {
          label = label.replace('>', '');
        }
        if (map[label] === undefined) {
          map[label] = {
            ...energyLabel,
            label: energyLabel.label
          };
        } else {
          map[label].percentage += energyLabel.percentage;
        }
        return map;
      },
      {}
    );

    return this.energyLabelSort.transform(
      Object.keys(reducedEnergyLabels).map((key: string) => {
        const rawLabel = reducedEnergyLabels[key].label;
        const topLetterScaleABand = this.combineAOrHigher() && this.isEpcAOrHigherLetterScaleLabel(rawLabel);
        const isAOrHigherBand = hasOneOrMoreLabelsHigherThanA && (key === 'a' || topLetterScaleABand);
        const translation =
          rawLabel === 'none'
            ? this.translateService.instant('energyLabel.EPC.none')
            : isAOrHigherBand
              ? this.translateService.instant('energyLabel.EPC.aOrHigher')
              : rawLabel;

        const energyLabelColorClass = (
          this.combineAOrHigher() && (key === 'a' || topLetterScaleABand)
            ? 'a'
            : rawLabel.search(/^(?:\d{3}-\d{3}|>\d{3,4})$/) > -1
              ? rawLabel.replace('>', '')
              : rawLabel === 'none'
                ? rawLabel
                : normalizeEpcLabelForA(rawLabel)[0]
        ).toLowerCase();
        return {
          percentage: reducedEnergyLabels[key].percentage,
          colorClass: `energy-label-epc-bg-${energyLabelColorClass}`,
          label: rawLabel,
          translation: translation,
          epcAOrHigherBand: isAOrHigherBand
        };
      }),
      labelGroup,
      'label',
      true
    );
  }
}
